(ns ncr.eventlog
  (:require
   ["bignumber.js" :as big]
   ["@js-joda/core" :refer [LocalDate]]
   [clojure.string :as str]
   [com.rpl.specter :as sp]
   [cljs.core.async :as async :refer-macros [go]]
   [tick.core :as t]
   [ncr.fragment.attachment-label :as al]
   [uix.core.alpha :as uix]
   [ncr.fragment.props.date :as date]
   [ncr.fragment.props.money :as money]
   [ncr.etc.util :as u]
   [ncr.etc.modal :as m]
   [ncr.me :as me]
   [ncr.widgets.core :as w]
   [ncr.etc.pf :as pf]
   [ncr.etc.profile-pspec :as pp]
   [ncr.backend :as b]
   [ncr.fragment.common]))

(defn- setval-with [p f structure]
  (sp/setval p (f structure) structure))

(defn- xform-eventlog [e]
  (->> e
    (sp/transform [sp/MAP-KEYS sp/NAME] #(str/replace % "_" "-"))
    (sp/transform [:data] u/transit-read)
    (sp/transform [:created] b/parse-utc-date-time)
    (sp/transform [:domain] keyword)
    (sp/transform [:event-type] keyword)
    (setval-with [:kw] #(keyword (sp/select-one! [:domain] %) (sp/select-one! [:event-type] %)))))

(def MAX-LABELS 5)

(defn c-assist-icon [{:keys [data]}]
  (let [{:keys [suggestions status]} data]
    (case status
      :ok
      (when (seq suggestions)
        [w/c-mdi-icon
         {:class [:ncr-c-attachment__icon :pf-u-success-color-100]
          :title "AI Suggestions"
          :icon :wizard-hat}])
      :progress
      [pf/c-spinner
       {:size :sm :class :pf-u-ml-sm :title "AI Assist is running..."}]

      nil)))

(defn- attachment-icons [{:keys [metadata auxdata]}]
  (let [{:keys [datev assist]} auxdata
        {:keys [textract]} metadata]
    (filter identity
      [(when datev
         [al/c-datev-icon {} datev])
       (when textract
         [al/c-ocr-icon {} textract])
       (when assist
         [c-assist-icon {:data assist}])])))

(defn c-attachment-icons [attachment]
  (when-let [icons (seq (attachment-icons attachment))]
    (into [pf/l-flex {:class-name :ncr-c-attachment-icons :space-items :space-items-none}]
      (map
        (fn [icon]
          (conj
            [pf/l-flex-item {:class-name :ncr-c-attachment-icons__icon}] icon))
        icons))))

(defn- attachment-label [{:keys [file-name] :as data}]
  [pf/c-label
   {:class :ncr-c-attachment-label
    :icon (c-attachment-icons data)}
   file-name])

(defn- user-label [{:keys [id]}]
  (if-let [user (me/user-by-id (uix/context me/*context*) id)]
    [pf/c-chip {:is-read-only true} (:name user)]
    [:i (pr-str id) (pr-str (uix/context me/*context*))]))

(defn- property->str [property]
  (cond
    (nil? property) "none"
    (instance? LocalDate property) (date/to-string property)
    (instance? big/BigNumber property) (money/to-string property)
    :else (pr-str property)))

(defn- render-property [key value]
  [:<> [:b (name key)] " " [:small "(" (property->str value) ")"]])

(defn- enumerate-properties [{:keys [prop-keys render]}]
  (let [items (filter some? (map render prop-keys))]
    (when (seq items)
      (if (> (count items) 1)
        [:<>
         (for [[idx item] (map-indexed vector (butlast items)) :let [max-idx (- (count items) 2)]]
           [:<> item (if (< idx max-idx) ", " " ")])
         "and " (last items)]
        (first items)))))

(defn- user-labels [{:keys [ids]}]
  (enumerate-properties
    {:prop-keys ids
     :render
     (fn [id]
       [user-label {:key id :id id}])}))

(defn- render-permissions [{:keys [owner account] :as data}]
  (enumerate-properties
    {:prop-keys [:read-acl :write-acl :owner]
     :render
     (fn [key]
       (let [v (get data key)]
         (case key
           :read-acl (when (seq v) [:<> [:b "read access"] " for" [user-labels {:ids v}]])
           :write-acl (when (seq v) [:<> [:b "write access"] " for " [user-labels {:ids v}]])
           :owner (when (not= owner account) [:<> [:b "changed owner"] " to " [user-label {:id v}]])
           nil)))}))

(defn debug-view [{:keys [domain event-type data] :as e}]
  [:<> {:title (pr-str e)} (pr-str [domain event-type]) " " (pr-str data)])

(defmulti c-event :kw)

(defmethod c-event :default [{:keys [domain event-type data] :as e}]
  [:<> {:title (pr-str e)} (pr-str [domain event-type]) " " (pr-str data)])

(defmethod c-event :reference/promote [{:keys [data account]}] ; "promote" is old name of "accept"
  [:<> "promoted document"
   (when-let [component (render-permissions (assoc data :account account))]
     [:<> " with " component])])

(defmethod c-event :reference/accept [{:keys [data account]}]
  [:<> "promoted document"
   (when-let [component (render-permissions (assoc data :account account))]
     [:<> " with " component])])

(defmethod c-event :reference/create [{:keys [data]}]
  (let [{nm :name :keys [labels folder]} data]
    [:<>
     "created " [:b nm]
     (when (seq labels)
       [:<>
        " with labels "
        [ncr.fragment.common/c-labels {:num-labels MAX-LABELS} (select-keys data [:labels])]])
     (when (and folder (me/folder-by-id (uix/context me/*context*) folder))
       [:<> " in " [ncr.fragment.common/c-folder (select-keys data [:folder])]])]))

(defmethod c-event :reference/update [{:keys [data]}]
  [:<> "updated "
   [enumerate-properties {:prop-keys (keys data) :render (fn [k] [:b (name k)])}]
   " for " [:b (:name data)]])

(defmethod c-event :reference/update-access [{:keys [data account]}]
  [:<> "set " [render-permissions (assoc data :account account)]])

(defmethod c-event :reference/patch-properties [{:keys [data]}]
  (when-not (and (contains? data :datev/start-date) (nil? (:datev/start-date data)))
    [:<> ; TODO(df): look into this
     "updated "
     [enumerate-properties {:prop-keys (keys data) :render #(render-property % (get data %))}]]))

(defmethod c-event :reference/edit-labels [{:keys [data]}]
  [:<> (condp = (:op data)
        :set "set "
        :remove "removed "
        :add "added "
        "edited ")
   [ncr.fragment.common/c-labels {:num-labels MAX-LABELS} (select-keys data [:labels])]])

(defmethod c-event :reference/move [{:keys [data]}]
  [:<>
   "moved to "
   [ncr.fragment.common/c-folder (select-keys data [:folder])]])

(defmethod c-event :reference/attachment-attach [{:keys [data]}] ; TODO(df): "attach" is old version of "create"
  [:<> "added " [attachment-label data]])

(defmethod c-event :attachment/create [{:keys [data]}]
  [:<> "added " [attachment-label data]])

(defmethod c-event :attachment/detach [{:keys [data]}]
  [:<>
   [:span {:class :pf-u-danger-color-100} "permanently deleted "]
   [attachment-label data]])

(defmethod c-event :attachment/set-deleted [{:keys [data]}]
  [:<>
   "marked as "
   (when-not (:deleted data) "un")
   "deleted " [attachment-label data]])

(defmethod c-event :attachment/rename [{:keys [data]}]
  [:<>
   "renamed "
   [attachment-label (assoc data :file-name (:old-file-name data))]
   " to "
   [attachment-label data]])

(defmethod c-event :attachment/update-auxdata [{:keys [data]}]
  (let [{:keys [assist]} (:auxdata data)]
    (when (= :ok (:status assist))
      [:<> "processed " [attachment-label data] " by AI with "
       [:b (count (:suggestions assist))] " suggestions"])))

(defmethod c-event :attachment/update-metadata [{:keys [data]}]
  (when (= :ok (get-in data [:metadata :textract :status]))
    [:<> "processed " [attachment-label data] " by OCR"]))

(defn- modal-header [{:keys [title show-toolbar? show-all? on-toggle-show-all full-dates? on-toggle-full-dates]}]
  [:<>
   [:h1 {:class :pf-c-modal-box__title}
    [:span {:class :pf-c-modal-box__title-text} title]]
   (when show-toolbar?
     [pf/c-toolbar {:class :ncr-u-border-bottom :style {:padding-bottom 0 :margin-right -48}}
      [pf/c-toolbar-content {:style {:padding 0}}
       [pf/c-toolbar-group
        {:variant :icon-button-group
         :alignment {:default :align-right}}
        [w/c-toolbar-button
         {:style {:opacity (if full-dates? 1 "var( --ncr-inactive-icon-opacity)")}
          :icon :clock-outline
          :on-click on-toggle-full-dates
          :tooltip "Show full date"}]
        [w/c-toolbar-button
         {:style {:opacity (if show-all? 1 "var( --ncr-inactive-icon-opacity)")}
          :icon :eye-outline
          :on-click on-toggle-show-all
          :tooltip "Show all events"}]]]])])

(defn- events-list [{:keys [events show-all? full-dates?]}]
  (let [me (uix/context me/*context*)]
    [:dl {:class :ncr-c-events-list}
     (for [{:keys [id account created] :as event} events
           :let [component (or (c-event event) (when show-all? (debug-view event)))]]
       (when component
         [:<> ^{:key id}
          [:dt {:class :ncr-c-events-list__header}
           [:span {:class :ncr-c-events-list__header_name}
            (or (:name (and account (me/user-by-id me account))) [:i "Neckar"])]
           " "
           (if full-dates?
             [:span {:class :pf-c-timestamp :style {:margin-left 5}} (t/format w/default-formatter created)]
             [w/c-date-distance {:class :pf-c-timestamp} created])]
          [:dd component]]))]))

(defn- process-test-events [events]
  (map (fn [[[domain event-type] data]]
         {:data data :domain domain :event-type event-type
          :kw (keyword (name domain) (name event-type))})
    (partition 2 events)))

(defn- dialog-body [domain domain-id]
  (let [*data (uix/state {})
        {:keys [error events]} @*data
        *show-all? (pp/use-fragment-history-show-all)
        show-all? @*show-all?
        *full-dates? (pp/use-fragment-history-full-dates)
        full-dates? @*full-dates?]

    (uix/effect!
      (fn []
        (go
          (u/if-ok [result
                    (<! (b/!query
                          [:eventlog
                           {:domain (name domain)
                            :domainId domain-id}
                           [:id :domain
                            :account :event_type :data :created]]))]
            (reset! *data {:events (mapv xform-eventlog result)})
            (reset! *data {:error result}))))
      [domain domain-id])

    [m/c-modal
     {:id :history-dialog
      :variant :large
      :header
      [modal-header
       {:title "History"
        :show-toolbar? (seq events)
        :show-all? show-all?
        :on-toggle-show-all #(reset! *show-all? (not show-all?))
        :full-dates? full-dates?
        :on-toggle-full-dates #(reset! *full-dates? (not full-dates?))}]
      :actions
      [[pf/c-button
        {:variant :link :on-click #(m/close! nil)} "Close"]]}
     ;TODO(df): add no events Empty State view
     (cond
       error
       [:p "Error: " (pr-str [domain domain-id]) (pr-str error)]

       events
       [events-list {:events events :show-all? show-all? :full-dates? full-dates?}]

       :else
       [w/c-empty-progress "Loading..."])]))

(defn !show [domain domain-id]
  (go (<! (m/!modal #'dialog-body domain domain-id))))
